package com.trevorwiebe.timesheet.admin.loadrate.presentation

import com.trevorwiebe.timesheet.core.presentation.DisposableViewModel
import androidx.lifecycle.viewModelScope
import com.trevorwiebe.timesheet.admin.loadrate.domain.LoadRateRepository
import com.trevorwiebe.timesheet.admin.loadrate.domain.model.LoadRate
import com.trevorwiebe.timesheet.admin.loadrate.domain.model.LoadRateType
import com.trevorwiebe.timesheet.core.domain.dollarsToCents
import com.trevorwiebe.timesheet.mileage.domain.LocationRepository
import com.trevorwiebe.timesheet.mileage.domain.model.Location
import com.trevorwiebe.timesheet.mileage.domain.model.LocationType
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

class LoadRateViewModel(
    private val loadRateRepository: LoadRateRepository,
    private val locationRepository: LocationRepository,
) : DisposableViewModel() {

    private val _state = MutableStateFlow(LoadRateState())
    val state = _state.asStateFlow()

    /** Single choke point for state changes: keeps [LoadRateState.visibleLoadRates] in sync. */
    private fun updateState(transform: (LoadRateState) -> LoadRateState) {
        _state.update { current ->
            val next = transform(current)
            next.copy(visibleLoadRates = filterAndSortLoadRates(next))
        }
    }

    private fun filterAndSortLoadRates(state: LoadRateState): List<LoadRate> {
        val locationsById = state.locations.associateBy { it.id }

        val byCategory = if (state.activeFilters.isEmpty()) {
            state.loadRates
        } else {
            state.loadRates.filter { it.rateCategory in state.activeFilters }
        }

        val query = state.searchQuery.trim()
        val bySearch = if (query.isEmpty()) {
            byCategory
        } else {
            byCategory.filter { rate ->
                listOfNotNull(
                    locationsById[rate.pickupLocationId],
                    locationsById[rate.dropLocationId],
                ).any { location ->
                    location.name.contains(query, ignoreCase = true) ||
                        location.city.contains(query, ignoreCase = true)
                }
            }
        }

        return bySearch.sortedWith(
            compareBy(
                { locationsById[it.pickupLocationId]?.name?.lowercase() ?: "" },
                { locationsById[it.dropLocationId]?.name?.lowercase() ?: "" },
            )
        )
    }

    init {
        loadLoadRates()
        loadLocations()
    }

    fun onEvent(event: LoadRateEvent) {
        when (event) {
            is LoadRateEvent.OnAddClick -> {
                updateState { it.copy(showAddEditDialog = true, editingLoadRate = null) }
            }
            is LoadRateEvent.OnEditClick -> {
                updateState { it.copy(showAddEditDialog = true, editingLoadRate = event.loadRate) }
            }
            is LoadRateEvent.OnDismissDialog -> {
                updateState { it.copy(showAddEditDialog = false, editingLoadRate = null) }
            }
            is LoadRateEvent.OnSave -> {
                saveLoadRate(event)
            }
            is LoadRateEvent.OnSearchQueryChanged -> {
                updateState { it.copy(searchQuery = event.value) }
            }
            is LoadRateEvent.OnFilterToggled -> {
                updateState {
                    val filters = if (event.category in it.activeFilters) {
                        it.activeFilters - event.category
                    } else {
                        it.activeFilters + event.category
                    }
                    it.copy(activeFilters = filters)
                }
            }
            is LoadRateEvent.OnErrorShown -> {
                updateState { it.copy(error = null) }
            }
        }
    }

    private fun loadLoadRates() {
        viewModelScope.launch {
            updateState { it.copy(isLoading = true) }
            loadRateRepository.getLoadRates().collect { result ->
                if (result.error != null) {
                    updateState { it.copy(isLoading = false, error = result.error) }
                } else {
                    @Suppress("UNCHECKED_CAST")
                    val loadRates = result.data as List<LoadRate>
                    updateState { it.copy(isLoading = false, loadRates = loadRates, error = null) }
                }
            }
        }
    }

    private fun loadLocations() {
        viewModelScope.launch {
            locationRepository.getLocations().collect { result ->
                @Suppress("UNCHECKED_CAST")
                val locations = (result.data as? List<Location> ?: emptyList())
                    .filter { !it.deleted && it.type == LocationType.DELIVERY }
                updateState { it.copy(locations = locations) }
            }
        }
    }

    private fun saveLoadRate(event: LoadRateEvent.OnSave) {
        val perUnit = event.perUnitText.dollarsToCents().coerceAtLeast(0)
        val flatRate = event.flatRateText.dollarsToCents().coerceAtLeast(0)
        val isPerUnit = event.rateType == LoadRateType.PER_UNIT
        if (isPerUnit && event.unit == null) {
            updateState { it.copy(error = "Per unit rates need a unit") }
            return
        }
        viewModelScope.launch {
            val editing = _state.value.editingLoadRate
            val loadRate = LoadRate(
                id = editing?.id ?: "",
                rateCategory = event.rateCategory,
                rateType = event.rateType,
                unit = if (isPerUnit) event.unit else null,
                perUnit = if (isPerUnit) perUnit else 0,
                flatRate = if (isPerUnit) 0 else flatRate,
                pickupLocationId = event.pickupLocationId,
                dropLocationId = event.dropLocationId,
            )
            val result = if (editing == null) {
                loadRateRepository.addLoadRate(loadRate)
            } else {
                loadRateRepository.updateLoadRate(loadRate)
            }
            if (result.error != null) {
                updateState { it.copy(error = result.error) }
            } else {
                updateState { it.copy(showAddEditDialog = false, editingLoadRate = null) }
            }
        }
    }
}
